import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { combineLatest } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { DecimalPipe } from '@angular/common';

@Component({
  selector: 'app-consumption-and-costs-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './consumption-and-costs-chart.component.html',
  styleUrl: './consumption-and-costs-chart.component.css'
})
export class ConsumptionAndCostsChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  labels: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  costsActualLabel: string = "Costs (Actual)";
  costsForecastLabel: string = "Costs (Forecast)";
  kwhActualLabel: string = "kWh (Actual)";
  kwhForecastLabel: string = "kWh (Forecast)";
  consumptionF1Label: string = "Consumption F1";
  consumptionF2Label: string = "Consumption F2";
  consumptionF3Label: string = "Consumption F3";
  consumptionF1ForecastLabel: string = "Consumption F1 (Forecast)";
  consumptionF2ForecastLabel: string = "Consumption F2 (Forecast)";
  consumptionF3ForecastLabel: string = "Consumption F3 (Forecast)";


  // Dummy data
  @Input() showDummyData: boolean = false;
  dummyconsumptionF1: (number | null)[] = [20, 30, 25, 35, 40, 28, 33, 45, 37, null, null, null];
  dummyconsumptionF2: (number | null)[] = [15, 25, 18, 22, 30, 24, 29, 32, 27, null, null, null];
  dummyconsumptionF3: (number | null)[] = [10, 18, 12, 15, 20, 14, 19, 25, 17, null, null, null];
  dummyconsumptionF1Forecast: (number | null)[] = [null, null, null, null, null, null, null, null, null, 42, 31, 39];
  dummyconsumptionF2Forecast: (number | null)[] = [null, null, null, null, null, null, null, null, null, 36, 21, 28];
  dummyconsumptionF3Forecast: (number | null)[] = [null, null, null, null, null, null, null, null, null, 22, 13, 16];
  dummycostsActual: (number | null)[] = [5.85, 9.49, 7.15, 9.36, 11.7, 8.58, null, null, null, null, null, null];
  dummycostsForecast: (number | null)[] = [null, null, null, null, null, 8.58, 6.93, 6.11, 7.02, 5.59, 6.5, 6.76];
  dummykWhActual: (number | null)[] = [45.0, 73.0, 55.0, 72.0, 90.0, 66.0, null, null, null, null, null, null];
  dummykWhForecast: (number | null)[] = [null, null, null, null, null, 66.0, 53.0, 47.0, 54.0, 43.0, 50.0, 52.0];

  @Input() consumptionF1: (number | null)[] = [20, 30, 25, 35, 40, 28, 33, 45, 37, null, null, null];
  @Input() consumptionF2: (number | null)[] = [15, 25, 18, 22, 30, 24, 29, 32, 27, null, null, null];
  @Input() consumptionF3: (number | null)[] = [10, 18, 12, 15, 20, 14, 19, 25, 17, null, null, null];
  @Input() consumptionF1Forecast: (number | null)[] = [null, null, null, null, null, null, null, null, null, 42, 31, 39];
  @Input() consumptionF2Forecast: (number | null)[] = [null, null, null, null, null, null, null, null, null, 36, 21, 28];
  @Input() consumptionF3Forecast: (number | null)[] = [null, null, null, null, null, null, null, null, null, 22, 13, 16];
  @Input() costsActual: (number | null)[] = [5.85, 9.49, 7.15, 9.36, 11.7, 8.58, null, null, null, null, null, null];
  @Input() costsForecast: (number | null)[] = [null, null, null, null, null, 8.58, 6.93, 6.11, 7.02, 5.59, 6.5, 6.76];
  @Input() kWhActual: (number | null)[] = [45.0, 73.0, 55.0, 72.0, 90.0, 66.0, null, null, null, null, null, null];
  @Input() kWhForecast: (number | null)[] = [null, null, null, null, null, 66.0, 53.0, 47.0, 54.0, 43.0, 50.0, 52.0];

  @Input() showForecastData: boolean = true;


  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    combineLatest([
      this.translocoService.selectTranslate('dashboard.costs_actual_label'),
      this.translocoService.selectTranslate('dashboard.costs_forecast_label'),
      this.translocoService.selectTranslate('dashboard.kwh_actual_label'),
      this.translocoService.selectTranslate('dashboard.kwh_forecast_label'),
      this.translocoService.selectTranslate('dashboard.consumption_f1_label'),
      this.translocoService.selectTranslate('dashboard.consumption_f2_label'),
      this.translocoService.selectTranslate('dashboard.consumption_f3_label'),
      this.translocoService.selectTranslate('dashboard.consumption_f1_forecast_label'),
      this.translocoService.selectTranslate('dashboard.consumption_f2_forecast_label'),
      this.translocoService.selectTranslate('dashboard.consumption_f3_forecast_label'),
    ]).subscribe((
      [
        costsActualTranslation,
        costsForecastTranslation,
        kwhActualTranslation,
        kwhForecastTranslation,
        consumptionF1Translation,
        consumptionF2Translation,
        consumptionF3Translation,
        consumptionF1ForecastTranslation,
        consumptionF2ForecastTranslation,
        consumptionF3ForecastTranslation,
      ]
    ) => {
      this.costsActualLabel = costsActualTranslation;
      this.costsForecastLabel = costsForecastTranslation;
      this.kwhActualLabel = kwhActualTranslation;
      this.kwhForecastLabel = kwhForecastTranslation;
      this.consumptionF1Label = consumptionF1Translation;
      this.consumptionF2Label = consumptionF2Translation;
      this.consumptionF3Label = consumptionF3Translation;
      this.consumptionF1ForecastLabel = consumptionF1ForecastTranslation;
      this.consumptionF2ForecastLabel = consumptionF2ForecastTranslation;
      this.consumptionF3ForecastLabel = consumptionF3ForecastTranslation;
    });

    const monthKeys = [
      'shared.months.jan',
      'shared.months.feb',
      'shared.months.mar',
      'shared.months.apr',
      'shared.months.may',
      'shared.months.jun',
      'shared.months.jul',
      'shared.months.aug',
      'shared.months.sep',
      'shared.months.oct',
      'shared.months.nov',
      'shared.months.dec',
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.labels = translations;
      this.updateChart();
    });
  }

  updateChart(): void {
    if (this.showDummyData) {
      this.consumptionF1 = this.dummyconsumptionF1;
      this.consumptionF2 = this.dummyconsumptionF2;
      this.consumptionF3 = this.dummyconsumptionF3;
      this.consumptionF1Forecast = this.dummyconsumptionF1Forecast;
      this.consumptionF2Forecast = this.dummyconsumptionF2Forecast;
      this.consumptionF3Forecast = this.dummyconsumptionF3Forecast;
      this.costsActual = this.dummycostsActual;
      this.costsForecast = this.dummycostsForecast;
      this.kWhActual = this.dummykWhActual;
      this.kWhForecast = this.dummykWhForecast;
    }

    Chart.register(ChartDataLabels);

    this.data = {
      labels: this.labels,
      datasets: [
        {
          type: 'line',
          label: this.kwhActualLabel,
          borderColor: '#9ACD32',
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          data: this.kWhActual,
          pointStyle: 'circle',
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: '#9ACD32',
          datalabels: {
            display: 'auto',
            color: '#000',
            clamp: true,
            align: 'top',
            anchor: 'end',
            font: { size: 10, family: 'Gilroy' },
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0')} kWh` },
          },
          xAxisID: 'x2',
          yAxisID: 'y2',
        },
        {
          type: 'line',
          label: this.kwhForecastLabel,
          borderColor: '#9ACD32',
          borderDash: [5, 5],
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          data: this.kWhForecast,
          pointStyle: 'circle',
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBackgroundColor: '#9ACD32',
          datalabels: {
            display: 'auto',
            color: '#000',
            clamp: true,
            align: 'top',
            anchor: 'end',
            font: { size: 10, family: 'Gilroy' },
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0')} kWh` },
          },
          xAxisID: 'x2',
          yAxisID: 'y2',
        },
        {
          type: 'bar',
          label: this.consumptionF1Label,
          backgroundColor: '#A9BE92',
          data: this.consumptionF1,
          stack: 'consumption',
          borderRadius: 12,
          datalabels: {
            display: 'auto',
            color: '#000',
            font: { size: 10, family: 'Gilroy' },
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0')} kWh` },
          },
        },
        {
          type: 'bar',
          label: this.consumptionF2Label,
          backgroundColor: '#BDCAAC',
          data: this.consumptionF2,
          stack: 'consumption',
          borderRadius: 12,
          datalabels: {
            display: 'auto',
            color: '#000',
            font: { size: 10, family: 'Gilroy' },
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0')} kWh` },
          },
        },
        {
          type: 'bar',
          label: this.consumptionF3Label,
          backgroundColor: '#D4DFC9',
          data: this.consumptionF3,
          stack: 'consumption',
          borderRadius: 12,
          datalabels: {
            display: 'auto',
            color: '#000',
            font: { size: 10, family: 'Gilroy' },
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0')} kWh` },
          }
        },
      ],
    };

    if (this.showForecastData) {
      this.data.datasets.push(
        {
          type: 'bar',
          label: this.consumptionF1ForecastLabel,
          backgroundColor: '#D5E2C5',
          data: this.consumptionF1Forecast,
          stack: 'consumption',
          borderRadius: 12,
          datalabels: {
            display: 'auto',
            color: '#000',
            font: { size: 10, family: 'Gilroy' },
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0')} kWh` },
          },
        },
        {
          type: 'bar',
          label: this.consumptionF2ForecastLabel,
          backgroundColor: '#E1E9CF',
          data: this.consumptionF2Forecast,
          stack: 'consumption',
          borderRadius: 12,
          datalabels: {
            display: 'auto',
            color: '#000',
            font: { size: 10, family: 'Gilroy' },
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0')} kWh` },
          },
        },
        {
          type: 'bar',
          label: this.consumptionF3ForecastLabel,
          backgroundColor: '#EFF4E4',
          data: this.consumptionF3Forecast,
          stack: 'consumption',
          borderRadius: 12,
          datalabels: {
            display: 'auto',
            color: '#000',
            font: { size: 10, family: 'Gilroy' },
            formatter: (value: any) => { if (value == 0 || value == null) { return ''; } return `${this.decimalPipe.transform(value, '1.0-0')} kWh` },
          }
        },
      );
    }

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            generateLabels: (chart: any) => {
              const datasets = chart.data.datasets;
              return datasets.map((dataset: any, index: any) => {
                return {
                  text: dataset.label,
                  fillStyle: dataset.type === 'bar' ? dataset.backgroundColor : dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  pointStyle: dataset.type === 'line' ? (dataset.borderDash ? 'dash' : 'line') : 'circle',
                  lineDash: dataset.type === 'line' && dataset.borderDash ? [2, 2, 2] : [],
                  lineWidth: dataset.type === 'line' ? 4 : 0,

                  hidden: !chart.isDatasetVisible(index),
                  datasetIndex: index,
                  type: dataset.type
                };
              }).filter((item: any) => item.type !== 'line');;
            },
            font: {
              size: 12,
            },
            color: '#000000',
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const index = context.dataIndex;
              let label = context.dataset.label || '';
              let value = context.raw || 0;
              return `${label}: ${this.decimalPipe.transform(value, '1.0-0') || value}`;
            }
          }
        },
      },
      scales: {
        x: {
          stacked: true,
          title: {
            display: false,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: {
            display: true,
          },
          border: {
            dash: [5, 5],
          },
          ticks: {
            display: false,
          },
        },
        x2: {
          beginAtZero: true,
          display: false,
        },
        y2: {
          beginAtZero: true,
          display: false,
        }
      },
    };
  }
}